import React, { useState, useCallback, useEffect } from 'react';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import ContentLayout from '@cloudscape-design/components/content-layout';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import Modal from '@cloudscape-design/components/modal';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Alert from '@cloudscape-design/components/alert';
import Spinner from '@cloudscape-design/components/spinner';
import type { User, UserRole } from '../types';
import { usersService } from '../services/users';

/**
 * Role options for the role assignment dropdown.
 */
const ROLE_OPTIONS: SelectProps.Option[] = [
  { label: 'User', value: 'user' },
  { label: 'Admin', value: 'admin' },
];

/**
 * Get the role option object for a given role value.
 *
 * @param role - The user role value
 * @returns The corresponding SelectProps.Option
 */
function getRoleOption(role: UserRole): SelectProps.Option {
  return ROLE_OPTIONS.find((option) => option.value === role) ?? ROLE_OPTIONS[0];
}

/**
 * Admin User Management Page - Admin panel for user management
 *
 * Displays a table of all platform users with management capabilities:
 * - Users table with email, status (enabled/disabled), and role columns
 * - Disable/enable action buttons for each user
 * - Role assignment dropdown for changing user roles
 * - Confirmation modal before disabling a user
 * - Loading spinner while fetching users from API
 * - Error alert when API requests fail
 *
 * Features:
 * - Uses Cloudscape Design System components
 * - Fetches users from real API via usersService
 * - Status indicators for enabled/disabled state
 * - Inline role editing with Select dropdown
 * - Confirmation dialog for destructive actions
 *
 * @validates Requirements 5.3 - Fetch data from real API instead of inline mock data
 * @validates Requirements 5.4 - Display loading indicators while API requests are in progress
 * @validates Requirements 5.5 - Display error states when API requests fail
 * @validates Requirements 4.6 - Display error alert when API request returns non-2xx
 * @validates Requirements 4.7 - Do not silently display empty/mock data on failure
 */
export function AdminUserManagementPage(): React.ReactElement {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disableModalVisible, setDisableModalVisible] = useState(false);
  const [userToDisable, setUserToDisable] = useState<User | null>(null);

  /**
   * Load users from the API.
   * Sets loading state during fetch and error state on failure.
   */
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await usersService.getUsers();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  /**
   * Handle role change for a user.
   * Updates the user's role via API and refreshes local state.
   *
   * @param userId - The ID of the user to update
   * @param newRole - The new role to assign
   */
  const handleRoleChange = useCallback(async (userId: string, newRole: UserRole): Promise<void> => {
    try {
      await usersService.updateUser(userId, { role: newRole });
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, role: newRole } : user
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    }
  }, []);

  /**
   * Handle enable action for a user.
   * Enables the user account via API.
   *
   * @param userId - The ID of the user to enable
   */
  const handleEnableUser = useCallback(async (userId: string): Promise<void> => {
    try {
      await usersService.updateUser(userId, { enabled: true });
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, enabled: true } : user
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable user');
    }
  }, []);

  /**
   * Initiate the disable user flow.
   * Shows a confirmation modal before disabling.
   *
   * @param user - The user to disable
   */
  const handleDisableClick = useCallback((user: User): void => {
    setUserToDisable(user);
    setDisableModalVisible(true);
  }, []);

  /**
   * Confirm and execute the disable action.
   * Called when user confirms in the modal. Sends update to API.
   */
  const handleConfirmDisable = useCallback(async (): Promise<void> => {
    if (userToDisable) {
      try {
        await usersService.updateUser(userToDisable.id, { enabled: false });
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userToDisable.id ? { ...user, enabled: false } : user
          )
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to disable user');
      }
    }
    setDisableModalVisible(false);
    setUserToDisable(null);
  }, [userToDisable]);

  /**
   * Cancel the disable action.
   * Closes the modal without making changes.
   */
  const handleCancelDisable = useCallback((): void => {
    setDisableModalVisible(false);
    setUserToDisable(null);
  }, []);

  // Show full-page spinner during initial load
  if (loading && users.length === 0) {
    return (
      <ContentLayout
        data-testid="admin-user-management-page"
        header={
          <Header
            variant="h1"
            description="Manage platform users and their access"
          >
            User Management
          </Header>
        }
      >
        <Box textAlign="center" margin={{ vertical: 'xxl' }}>
          <Spinner size="large" />
          <Box variant="p" margin={{ top: 's' }}>Loading users...</Box>
        </Box>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      data-testid="admin-user-management-page"
      header={
        <Header
          variant="h1"
          description="Manage platform users and their access"
        >
          User Management
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert
            type="error"
            dismissible
            onDismiss={() => setError(null)}
            header="Error"
            data-testid="error-alert"
            action={
              <Button onClick={loadUsers}>Retry</Button>
            }
          >
            {error}
          </Alert>
        )}

        <Table
          data-testid="users-table"
          columnDefinitions={[
            {
              id: 'email',
              header: 'Email',
              cell: (item: User) => item.email,
              sortingField: 'email',
              width: 250,
            },
            {
              id: 'status',
              header: 'Status',
              cell: (item: User) => (
                <StatusIndicator
                  type={item.enabled ? 'success' : 'stopped'}
                  data-testid={`status-indicator-${item.id}`}
                >
                  {item.enabled ? 'Enabled' : 'Disabled'}
                </StatusIndicator>
              ),
              sortingField: 'enabled',
              width: 120,
            },
            {
              id: 'role',
              header: 'Role',
              cell: (item: User) => (
                <Select
                  selectedOption={getRoleOption(item.role)}
                  onChange={({ detail }) =>
                    handleRoleChange(item.id, detail.selectedOption.value as UserRole)
                  }
                  options={ROLE_OPTIONS}
                  selectedAriaLabel="Selected"
                  data-testid={`role-select-${item.id}`}
                />
              ),
              width: 150,
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: (item: User) => (
                <SpaceBetween direction="horizontal" size="xs">
                  {item.enabled ? (
                    <Button
                      variant="normal"
                      onClick={() => handleDisableClick(item)}
                      data-testid={`disable-button-${item.id}`}
                    >
                      Disable
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={() => handleEnableUser(item.id)}
                      data-testid={`enable-button-${item.id}`}
                    >
                      Enable
                    </Button>
                  )}
                </SpaceBetween>
              ),
              width: 120,
            },
          ]}
          items={users}
          loading={loading}
          loadingText="Loading users..."
          trackBy="id"
          variant="container"
          header={
            <Header
              variant="h2"
              description="View and manage user accounts"
              counter={`(${users.length})`}
            >
              Users
            </Header>
          }
          empty={
            <Box
              margin={{ vertical: 'xs' }}
              textAlign="center"
              color="inherit"
            >
              <SpaceBetween size="m">
                <b>No users found</b>
                <Box variant="p" color="inherit">
                  There are no users registered on the platform.
                </Box>
              </SpaceBetween>
            </Box>
          }
        />
      </SpaceBetween>

      {/* Confirmation Modal for Disabling Users */}
      <Modal
        visible={disableModalVisible}
        onDismiss={handleCancelDisable}
        header="Confirm Disable User"
        closeAriaLabel="Close modal"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={handleCancelDisable}
                data-testid="cancel-disable-button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmDisable}
                data-testid="confirm-disable-button"
              >
                Disable User
              </Button>
            </SpaceBetween>
          </Box>
        }
        data-testid="disable-confirmation-modal"
      >
        <SpaceBetween size="m">
          <Box>
            Are you sure you want to disable the following user?
          </Box>
          {userToDisable && (
            <Box variant="p">
              <strong>Email:</strong> {userToDisable.email}
            </Box>
          )}
          <Box variant="p" color="text-body-secondary">
            Disabled users will not be able to access the platform until re-enabled.
          </Box>
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );
}

export default AdminUserManagementPage;
